/** D-126: add a client by hand (CRM → « New client »). Consent of law 2024/017 recorded by the
 * agent with the date; a known e-mail or number shows the existing client (open their sheet, or
 * turn their lead into a client) instead of creating a duplicate. */

import {
  ApiError,
  type ConsentSourceEnum,
  crmContactsDuplicatesList,
  type Duplicate,
  type ContactLanguageEnum,
  type SourceEnum,
  useCatalogServicesList,
  useCrmContactsNewClientCreate,
  useCrmLeadsMoveCreate,
} from "@agencyos/api-client";
import { countryOptions } from "@agencyos/core";
import { Button, GlassSheet, SelectField, SoftToggle, TextAreaField, TextField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { localized } from "../../components/TranslatedFields";
import { fieldErrors } from "../../lib/settings";

const SOURCES: SourceEnum[] = ["walk_in", "phone", "whatsapp", "facebook", "instagram", "website", "referral", "other"];
const CONSENT_SOURCES: ConsentSourceEnum[] = ["office", "phone", "whatsapp", "web_form", "other"];
const LANGUAGES: ContactLanguageEnum[] = ["fr", "en", "pcm"];

function DuplicateNotice({ rows, onConverted }: { rows: readonly Duplicate[]; onConverted: (contactId: string) => void }) {
  const { t } = useTranslation();
  const move = useCrmLeadsMoveCreate();
  return (
    <div role="alert" className="duplicate-notice">
      <strong>{t("clients.duplicate.title")}</strong>
      {rows.map((row) => (
        <div key={row.id} className="stack">
          <span>
            {row.full_name || "—"} · {row.match.map((m) => t(`clients.duplicate.match.${m}`)).join(", ")}
            {" · "}
            {row.is_client ? t("clients.duplicate.isClient") : row.lead_stage ? t("clients.duplicate.isLead", { stage: t(`crm.stage.${row.lead_stage}`) }) : ""}
          </span>
          {row.visible ? (
            <div className="row">
              <Link className="link" to={`/clients/${row.id}`}>
                {t("clients.duplicate.open")}
              </Link>
              {!row.is_client && row.lead_id ? (
                <Button
                  variant="secondary"
                  disabled={move.isPending}
                  onClick={() => move.mutate({ id: row.lead_id as string, data: { stage: "client" } }, { onSuccess: () => onConverted(row.id) })}
                >
                  {t("clients.duplicate.convert")}
                </Button>
              ) : null}
            </div>
          ) : (
            <span className="muted">{t("clients.duplicate.otherAdvisor")}</span>
          )}
        </div>
      ))}
    </div>
  );
}

export function NewClientSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const services = useCatalogServicesList(undefined, { query: { enabled: open } });
  const create = useCrmContactsNewClientCreate();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_e164: "",
    country: "CM",
    city: "",
    language: (i18n.language === "en" ? "en" : "fr") as ContactLanguageEnum,
    source: "walk_in" as SourceEnum,
    service: "",
    notes: "",
    consent_source: "office" as ConsentSourceEnum,
  });
  const [consent, setConsent] = useState(false);
  const [missing, setMissing] = useState<Record<string, string>>({});
  const [duplicates, setDuplicates] = useState<readonly Duplicate[]>([]);
  const set = (key: keyof typeof form) => (value: string) => setForm((f) => ({ ...f, [key]: value }));
  const errors = { ...fieldErrors(create.error), ...missing };

  const done = (contactId: string) => {
    void queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0] ?? "").startsWith("/api/v1/crm/") });
    onClose();
    navigate(`/clients/${contactId}`);
  };

  /** Live check on leaving the e-mail or phone field. */
  const check = async () => {
    const email = form.email.trim();
    const phone = form.phone_e164.trim();
    if (!email && phone.replace(/\D/g, "").length < 6) return setDuplicates([]);
    try {
      const response = await crmContactsDuplicatesList({ ...(email ? { email } : {}), ...(phone ? { phone } : {}) });
      setDuplicates(response.status === 200 ? response.data : []);
    } catch {
      setDuplicates([]);
    }
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!form.first_name.trim()) next.first_name = t("clients.new.firstNameRequired");
    if (!form.email.trim() && !form.phone_e164.trim()) next.phone_e164 = t("clients.new.contactRequired");
    if (!consent) next.consent = t("crm.consentRequired");
    setMissing(next);
    if (Object.keys(next).length) return;
    create.mutate(
      {
        data: {
          ...form,
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim(),
          phone_e164: form.phone_e164.trim(),
          service: form.service || null,
          consent: true,
        },
      },
      {
        onSuccess: (response) => {
          if (response.status === 201) done(response.data.contact.id);
        },
        onError: (error) => {
          if (error instanceof ApiError && error.status === 409) {
            const body = error.body as { duplicates?: Duplicate[] } | null;
            setDuplicates(body?.duplicates ?? []);
          }
        },
      },
    );
  };

  const serviceOptions = [
    { value: "", label: t("crm.noService") },
    ...(services.data?.status === 200
      ? services.data.data.results.map((s) => ({ value: s.id, label: `${s.code} — ${localized(s.name, i18n.language)}` }))
      : []),
  ];

  return (
    <GlassSheet open={open} title={t("clients.new.title")} onClose={onClose}>
      <form onSubmit={onSubmit} className="stack" noValidate>
        <p className="muted">{t("clients.new.intro")}</p>
        <div className="grid-2">
          <TextField label={t("crm.fields.firstName")} value={form.first_name} maxLength={100} autoComplete="off" error={errors.first_name} onChange={(e) => set("first_name")(e.target.value)} />
          <TextField label={t("crm.fields.lastName")} value={form.last_name} maxLength={100} autoComplete="off" error={errors.last_name} onChange={(e) => set("last_name")(e.target.value)} />
        </div>
        <TextField
          label={t("clients.fields.email")}
          type="email"
          inputMode="email"
          value={form.email}
          error={errors.email}
          onBlur={() => void check()}
          onChange={(e) => set("email")(e.target.value)}
        />
        <TextField
          label={t("crm.fields.phone")}
          type="tel"
          inputMode="tel"
          value={form.phone_e164}
          hint={t("clients.new.phoneHint")}
          error={errors.phone_e164}
          onBlur={() => void check()}
          onChange={(e) => set("phone_e164")(e.target.value)}
        />
        {duplicates.length ? <DuplicateNotice rows={duplicates} onConverted={done} /> : null}
        <div className="grid-2">
          <SelectField
            label={t("clients.fields.country")}
            value={form.country}
            error={errors.country}
            options={countryOptions(i18n.language)}
            onChange={(e) => set("country")(e.target.value)}
          />
          <TextField label={t("clients.fields.city")} value={form.city} maxLength={100} onChange={(e) => set("city")(e.target.value)} />
        </div>
        <div className="grid-2">
          <SelectField
            label={t("clients.fields.language")}
            value={form.language}
            options={LANGUAGES.map((l) => ({ value: l, label: t(`clients.language.${l}`) }))}
            onChange={(e) => set("language")(e.target.value)}
          />
          <SelectField
            label={t("crm.fields.source")}
            value={form.source}
            options={SOURCES.map((s) => ({ value: s, label: t(`crm.source.${s}`) }))}
            onChange={(e) => set("source")(e.target.value)}
          />
        </div>
        <SelectField label={t("clients.fields.service")} value={form.service} options={serviceOptions} onChange={(e) => set("service")(e.target.value)} />
        <TextAreaField label={t("clients.fields.notes")} rows={3} maxLength={4000} value={form.notes} onChange={(e) => set("notes")(e.target.value)} />
        <SoftToggle label={t("clients.new.consent")} checked={consent} onChange={setConsent} />
        {consent ? (
          <SelectField
            label={t("clients.fields.consentSource")}
            value={form.consent_source}
            options={CONSENT_SOURCES.map((s) => ({ value: s, label: t(`clients.consentSource.${s}`) }))}
            onChange={(e) => set("consent_source")(e.target.value)}
          />
        ) : null}
        {errors.consent ? (
          <p role="alert" className="ao-field__error">
            {errors.consent}
          </p>
        ) : null}
        {errors.non_field_errors || (create.error instanceof ApiError && create.error.status !== 409 && create.error.status !== 400) ? (
          <p role="alert" className="ao-field__error">
            {errors.non_field_errors ?? t("common.error")}
          </p>
        ) : null}
        <div className="row">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? t("common.loading") : t("clients.new.create")}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    </GlassSheet>
  );
}
